import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  ArrowLeft, Loader2, Bell, Mail, MessageSquare, Send, Plus, X, Save, Info
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminNotificationSettingsPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/settings');
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const isMaster = user?.role === 'master_admin';
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchCfg = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/settings/notifications`, { headers });
      setCfg(res.data);
    } catch (err) {
      toast.error('Failed to load notification settings');
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchCfg(); }, [fetchCfg]);

  const update = (patch) => setCfg(c => ({ ...c, ...patch }));

  const addEmail = () => {
    const e = newEmail.trim().toLowerCase();
    if (!e || !e.includes('@')) { toast.error('Enter a valid email'); return; }
    if ((cfg.notification_emails || []).includes(e)) { toast.error('Already in list'); return; }
    update({ notification_emails: [...(cfg.notification_emails || []), e] });
    setNewEmail('');
  };
  const removeEmail = (e) => {
    if ((cfg.notification_emails || []).length <= 1) { toast.error('At least one email is required'); return; }
    update({ notification_emails: cfg.notification_emails.filter(x => x !== e) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        email_enabled: cfg.email_enabled,
        sms_enabled: cfg.sms_enabled,
        in_app_enabled: cfg.in_app_enabled,
        customer_email_enabled: cfg.customer_email_enabled,
        customer_sms_enabled: cfg.customer_sms_enabled,
        notification_emails: cfg.notification_emails,
        notification_phone: cfg.notification_phone,
        business_phone_display: cfg.business_phone_display,
      };
      const res = await axios.put(`${API_URL}/api/admin/settings/notifications`, payload, { headers });
      setCfg(res.data);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/settings/notifications/test-email`, {}, { headers });
      const { sent, results } = res.data;
      if (sent > 0) toast.success(`Test email sent to ${sent} recipient${sent === 1 ? '' : 's'}`);
      else toast.error('Test failed — check your notification email(s) and Resend API key');
      // eslint-disable-next-line no-console
      console.log('Test results:', results);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test');
    } finally { setTesting(false); }
  };

  if (loading || !cfg) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" /></div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-notification-settings-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="sm" onClick={back} className="text-zinc-500" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
          </Button>
          {isMaster && (
            <Button variant="outline" onClick={sendTest} disabled={testing} data-testid="send-test-btn">
              {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send Test Notification
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3 mb-2">
          <Bell className="h-6 w-6 text-[#F56F05]" />
          <h1 className="text-3xl font-semibold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Lead Notifications</h1>
        </div>
        <p className="text-sm text-zinc-500 mb-6">Configure how your team is alerted when a new quote request comes in through the website.</p>

        {!isMaster && (
          <Card className="border-amber-200 bg-amber-50 shadow-none mb-4">
            <CardContent className="p-4 flex items-center gap-3">
              <Info className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">View-only — only Master Admin can edit these settings.</p>
            </CardContent>
          </Card>
        )}

        <div className="space-y-4">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Team Alerts</CardTitle>
              <CardDescription>Where to send new-lead alerts to your team.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ToggleRow label="Email admin(s) on new lead" checked={!!cfg.email_enabled} onChange={(v) => update({ email_enabled: v })} disabled={!isMaster} testid="toggle-email-admin" />
              <ToggleRow label="In-app dashboard notification" checked={!!cfg.in_app_enabled} onChange={(v) => update({ in_app_enabled: v })} disabled={!isMaster} testid="toggle-in-app" />
              <ToggleRow label="SMS admin(s) on new lead" checked={!!cfg.sms_enabled} onChange={(v) => update({ sms_enabled: v })} disabled={!isMaster} testid="toggle-sms-admin"
                helper="SMS provider not yet configured. Twilio credentials required." />

              <div>
                <Label className="text-xs">Notification emails</Label>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {(cfg.notification_emails || []).map(e => (
                    <div key={e} className="flex items-center gap-1.5 px-3 py-1 bg-zinc-100 rounded-full text-xs font-mono" data-testid={`email-chip-${e}`}>
                      <Mail className="h-3 w-3 text-zinc-500" /> {e}
                      {isMaster && (
                        <button type="button" onClick={() => removeEmail(e)} className="ml-1 hover:text-red-600" data-testid={`remove-email-${e}`}>
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {isMaster && (
                  <div className="flex gap-2 mt-2">
                    <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="another@yourdomain.com" className="text-sm" data-testid="new-email-input" />
                    <Button size="sm" variant="outline" onClick={addEmail} data-testid="add-email-btn">
                      <Plus className="h-4 w-4 mr-1" /> Add
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Notification phone (SMS target)</Label>
                  <Input value={cfg.notification_phone || ''} disabled={!isMaster}
                    onChange={e => update({ notification_phone: e.target.value })}
                    placeholder="+19715551234" data-testid="notification-phone" />
                </div>
                <div>
                  <Label className="text-xs">Display phone (shown in emails)</Label>
                  <Input value={cfg.business_phone_display || ''} disabled={!isMaster}
                    onChange={e => update({ business_phone_display: e.target.value })}
                    placeholder="(971) 715-0335" data-testid="business-phone-display" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Customer Auto-Reply</CardTitle>
              <CardDescription>What we send back to the customer after they submit the form.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ToggleRow label="Customer confirmation email" checked={!!cfg.customer_email_enabled} onChange={(v) => update({ customer_email_enabled: v })} disabled={!isMaster} testid="toggle-customer-email" />
              <ToggleRow label="Customer confirmation SMS" checked={!!cfg.customer_sms_enabled} onChange={(v) => update({ customer_sms_enabled: v })} disabled={!isMaster} testid="toggle-customer-sms"
                helper="Only sent when customer selects 'Text Message' as preferred contact. Twilio required." />
            </CardContent>
          </Card>
        </div>

        {isMaster && (
          <div className="flex justify-end mt-6">
            <Button className="bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={save} disabled={saving} data-testid="save-settings-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Settings
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const ToggleRow = ({ label, checked, onChange, disabled, helper, testid }) => (
  <div className="flex items-start gap-3">
    <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid}
      className="data-[state=checked]:bg-[#F56F05]" />
    <div className="flex-1 -mt-0.5">
      <Label className="text-sm cursor-pointer" onClick={() => !disabled && onChange(!checked)}>{label}</Label>
      {helper && <p className="text-[11px] text-zinc-500 mt-0.5">{helper}</p>}
    </div>
  </div>
);

export default AdminNotificationSettingsPage;
